import {FaqGroup} from "@/lib/filter-faqs";
import {Fragment, ReactElement, ReactNode, useState} from "react";
import {FormattedText} from "@/components/formatted-text";
import {FaqContent, Faqs, FaqTrigger, FaqItem} from "@/components/shadcn/ui/faqs";
import classNames from "classnames";
import {ChevronRight} from "lucide-react";

/**
 * Returns FAQs categorized in an accordion.
 *
 * @param term
 * @param items
 * @constructor
 */
export function FaqContainer({group, children}: {group: FaqGroup, children: ReactNode}): ReactElement {
  const [active, setActive] = useState(false);

  /**
   * Create a simple triangle.
   *
   * @constructor
   */
  function Triangle({active}: {active: boolean}): JSX.Element {
    return <span className={classNames(
      "flex pr-4 transition-all duration-500 ease-in-out",
      {"transform rotate-90 pr-6 pt-4": active},
    )}>
      <ChevronRight size={45}/>
    </span>
  }

  return (
    <div className={classNames("cursor-pointer")}>
      <button className={classNames("flex w-full h-full py-5", {"pb-0": active})} onClick={() => setActive(!active)} role="button">
        <h3 className="text-left font-light flex-grow"><FormattedText processed={group.title} /></h3>
        <Triangle active={active}/>
      </button>
      <div className={classNames(
        "overflow-hidden transition-all delay-200 ease-linear origin-top py-4",
        {"hidden": !active},
      )}>
        {children}
      </div>
    </div>
  );
}

/**
 * This follows the newer pattern of displaying FAQs from the FAQs vocabulary.
 * These need to first be run through the filterFaqs() library function before
 * being passed.
 *
 * @constructor
 */
export function WidgetFaqTerms({faqs}: { faqs: FaqGroup[] }) {
  return (
    <>
      {faqs.map((group, index) => (
        <Fragment key={`widget--faq-terms--${index}-${group.title}`}>
          {(faqs.length > 1 && group?.faqs?.length) ? (
            <FaqContainer group={group}>
              <Faqs type="single" collapsible className="md:px-8">
                {group.faqs.map((faq, index) => (
                  <Fragment key={`widget--faq-terms--term--${group.title}--${index}-${faq.drupal_internal__tid}`}>
                    <FaqItem value={faq.drupal_internal__tid}>
                      <FaqTrigger className="text-left text-base lg:text-xl pb-0"><div><FormattedText processed={faq.name}/></div></FaqTrigger>
                      {faq?.description?.['processed'] && (
                        <FaqContent className="text-lg pl-8 prose [&>*]:text-base lg:[&>*]:text-xl [&>ul]:font-light [&>ol]:font-light">
                          <FormattedText processed={faq.description['processed']}/>
                        </FaqContent>
                      )}
                    </FaqItem>
                  </Fragment>
                ))}
              </Faqs>
            </FaqContainer>
          ) : (
            <>
              {group?.faqs?.length > 1 ? (
                <Faqs type="single" collapsible className="px-8 py-4">
                  {group.faqs.map((faq, index) => (
                    <Fragment key={`widget--faq-terms--term--${group.title}--${index}-${faq.drupal_internal__tid}`}>
                      <FaqItem value={faq.drupal_internal__tid}>
                        <FaqTrigger className="text-left text-base lg:text-xl pb-0"><div><FormattedText processed={faq.name}/></div></FaqTrigger>
                        {faq?.description?.['processed'] && (
                          <FaqContent className="text-lg pl-8 prose [&>*]:text-base lg:[&>*]:text-xl [&>ul]:font-light [&>ol]:font-light">
                            <FormattedText processed={faq.description['processed']}/>
                          </FaqContent>
                        )}
                      </FaqItem>
                    </Fragment>
                  ))}
                </Faqs>
              ): <></>}
            </>
          )}
        </Fragment>
      ))}
    </>
  )
}